import type { CSSProperties } from "react";
import type { DetailJsonBlock, DetailJsonColumn, LayoutItem, UseBlockLayoutOptions } from "./type";

/** 解析 React 迁移期支持的静态值或 getter。 */
export const resolveBoolean = (value: boolean | (() => boolean) | undefined, fallback = true) => {
    if (value === undefined) return fallback;
    try {
        return typeof value === "function" ? Boolean(value()) : value;
    } catch {
        return false;
    }
};

const textWidth = (text: string) => {
    if (typeof document === "undefined") return text.length * 14;
    // jsdom 未实现 canvas，上层契约测试直接使用与 SSR 相同的稳定估算值。
    if (document.defaultView?.navigator.userAgent.includes("jsdom")) return text.length * 14;
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return text.length * 14;
    context.font = "14px sans-serif";
    return context.measureText(text).width;
};

/** 计算详情 JSON 的跨列布局和统一 label 宽度。 */
export const useBlockLayout = ({ columnCount, blocks, config }: UseBlockLayoutOptions) => {
    const blockLayoutMaps: LayoutItem[][] = blocks.map(block => {
        const result: LayoutItem[] = [];
        let cursor = 0;
        block.list?.forEach(item => {
            if (!resolveBoolean(item.show)) return;
            const span = Math.min(item.span ?? 1, columnCount);
            if (cursor + span > columnCount) cursor = 0;
            result.push({ colIndex: cursor, span });
            cursor = cursor + span >= columnCount ? 0 : cursor + span;
        });
        return result;
    });

    const columnLabelWidthMap: number[] = [];
    blocks.forEach((block, blockIndex) => {
        if (block.labelPlacement === "top") return;
        block.list?.forEach((item, itemIndex) => {
            if (!resolveBoolean(item.show)) return;
            const layout = blockLayoutMaps[blockIndex]?.[itemIndex];
            if (!layout) return;
            const width = item.width ?? (item.label ? textWidth(item.label) : 0);
            columnLabelWidthMap[layout.colIndex] = Math.max(columnLabelWidthMap[layout.colIndex] ?? 0, width);
        });
    });

    const getLabelStyle = (block: DetailJsonBlock, item: DetailJsonColumn, colIndex: number): CSSProperties => {
        if (block.labelPlacement === "top") return {};
        const maxWidth = item.labelMaxWidth ?? config?.labelMaxWidth;
        const width = Math.min(item.width ?? columnLabelWidthMap[colIndex] ?? 0, maxWidth ?? Number.POSITIVE_INFINITY);
        return {
            ...(width ? { width: `${width}px` } : {}),
            textAlign: item.labelAlign ?? "right",
            ...(maxWidth !== undefined ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {})
        };
    };

    return { blockLayoutMaps, columnLabelWidthMap, getLabelStyle };
};
